import type { BillingCycle, PlanCode } from './plans';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete';

export interface Subscription {
  tenantId: string;
  status: SubscriptionStatus;
  plan: PlanCode;
  billingCycle: BillingCycle;
  /** ISO timestamp; null once converted to paid. */
  trialEndsAt: string | null;
  /** ISO timestamp; null while trialing. */
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  canceledAt: string | null;
  paymentProvider: 'flouci' | 'konnect' | null;
  paymentProviderCustomerId: string | null;
  paymentProviderSubscriptionId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What the gate decides at any point in time.
 *
 *   full       — everything works (active sub or trial with days left)
 *   blocked    — paywall: can sign in, see billing, but no business pages
 *
 * (Intentionally kept binary — soft-block / read-only is a future option
 *  but multiplies the surface area we have to guard everywhere. Khalil
 *  picked hard-block in the planning step for clearer billing UX.)
 */
export type AccessLevel = 'full' | 'blocked';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days from `now` until `iso`. Negative if past. Floor so a tail of
 *  hours doesn't round up to a full day. Returns Number.NEGATIVE_INFINITY for
 *  null inputs so callers can safely compare. */
export function daysUntil(iso: string | null, now: Date = new Date()): number {
  if (!iso) return Number.NEGATIVE_INFINITY;
  const target = Date.parse(iso);
  if (Number.isNaN(target)) return Number.NEGATIVE_INFINITY;
  return Math.floor((target - now.getTime()) / MS_PER_DAY);
}

/** Convenience: trial days remaining, clamped to 0 if expired. Returns null
 *  when the subscription is not in trialing status. */
export function trialDaysRemaining(s: Subscription, now: Date = new Date()): number | null {
  if (s.status !== 'trialing') return null;
  const d = daysUntil(s.trialEndsAt, now);
  return Math.max(0, d);
}

export function isTrialing(s: Subscription): boolean {
  return s.status === 'trialing';
}

export function isActive(s: Subscription): boolean {
  return s.status === 'active';
}

export function isLifetimeSubscription(s: Subscription | null): boolean {
  return s?.status === 'active' && !s.currentPeriodEnd && !s.canceledAt;
}

export function hasPaymentMethod(s: Subscription): boolean {
  return s.paymentProvider !== null && s.paymentProviderCustomerId !== null;
}

/** Trial expired = status is trialing and trialEndsAt is in the past. */
function trialExpired(s: Subscription, now: Date): boolean {
  if (s.status !== 'trialing' || !s.trialEndsAt) return false;
  return Date.parse(s.trialEndsAt) <= now.getTime();
}

/** Paid period expired = status active/past_due and currentPeriodEnd in the past. */
function periodExpired(s: Subscription, now: Date): boolean {
  if (s.status !== 'active' && s.status !== 'past_due') return false;
  if (!s.currentPeriodEnd) return false;
  return Date.parse(s.currentPeriodEnd) <= now.getTime();
}

/** The single source of truth for "can this tenant use the app right now?"
 *
 *  Rules:
 *    - status='active' AND period not yet ended → full
 *    - status='trialing' AND trial not yet ended → full
 *    - status='canceled' AND currentPeriodEnd in the future → full (paid through)
 *    - everything else → blocked
 */
export function computeAccess(
  s: Subscription | null,
  now: Date = new Date(),
): AccessLevel {
  // No subscription row at all (race during onboarding, or backfill miss):
  // err on the side of the user — let them in. The seeder will catch up
  // on next page load.
  if (!s) return 'full';

  if (s.status === 'active' && !periodExpired(s, now)) return 'full';
  if (s.status === 'trialing' && !trialExpired(s, now)) return 'full';

  // User clicked Cancel but their period isn't over yet — they paid for it,
  // they keep access until the bill they already paid runs out.
  if (
    s.status === 'canceled' &&
    s.currentPeriodEnd &&
    Date.parse(s.currentPeriodEnd) > now.getTime()
  ) {
    return 'full';
  }

  return 'blocked';
}

export function isExpired(s: Subscription | null, now: Date = new Date()): boolean {
  return computeAccess(s, now) === 'blocked';
}
